import { useMemo, useState } from 'react'
import {
    Activity,
    ArrowRight,
    CalendarClock,
    CheckCircle2,
    Search,
    ShieldAlert,
    TrendingUp,
    User,
    Users,
} from 'lucide-react'
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import ModalCustom from '../../components/utils/ModalCustom.js';
import ShowUserDetails from './ShowUserDetails.js';
import toast from 'react-hot-toast';

const chartColors = ['#4f46e5', '#7c3aed', '#2563eb', '#db2777', '#64748b'];

const formatNumber = (value) => Number(value || 0).toLocaleString();

const parseDate = (value) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const getProvider = (user) => {
    if (Array.isArray(user.providerData) && user.providerData.length > 0) {
        return user.providerData[0]?.providerId || 'password';
    }

    return user.provider || 'password';
};

export default function DashboardHome({
    users,
    usersInfo = {},
    authChart = [],
    totalAuthRequests = 0,
    sections = [],
    setViewNumber,
}) {
    const [searchEmail, setSearchEmail] = useState('');
    const [selectedUserId, setSelectedUserId] = useState('');
    const [open, setOpen] = useState(false);

    const bannedEmails = ['barzoiu.alexandru@gmail.com'];

    const userList = useMemo(() => {
        const source = Object.keys(usersInfo).length ? usersInfo : Object.keys(users).reduce((acc, userId) => {
            acc[userId] = { userId, email: users[userId] };
            return acc;
        }, {});

        return Object.values(source).map((user) => ({
            ...user,
            userId: user.userId || user.uid,
            createdAt: parseDate(user.creationTime),
            lastLoginAt: parseDate(user.lastSignInTime),
            provider: getProvider(user),
        }));
    }, [users, usersInfo]);

    const overview = useMemo(() => {
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        const thirtyDaysAgo = now.getTime() - 30 * 24 * 60 * 60 * 1000;
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();

        const newToday = userList.filter((user) => user.createdAt?.getTime() >= startOfToday).length;
        const newThisMonth = userList.filter((user) => user.createdAt?.getTime() >= startOfMonth).length;
        const active30 = userList.filter((user) => user.lastLoginAt?.getTime() >= thirtyDaysAgo).length;
        const verified = userList.filter((user) => user.emailVerified).length;
        const disabled = userList.filter((user) => user.disabled).length;

        return {
            total: userList.length || totalAuthRequests,
            newToday,
            newThisMonth,
            active30,
            verified,
            disabled,
            verificationRate: userList.length ? Math.round((verified / userList.length) * 100) : 0,
        };
    }, [totalAuthRequests, userList]);

    const signupTrend = useMemo(() => {
        return authChart.slice(-18).map((item) => ({
            day: item.day,
            Users: item.Users || 0,
        }));
    }, [authChart]);

    const domainData = useMemo(() => {
        const counts = userList.reduce((acc, user) => {
            const domain = user.email?.includes('@') ? user.email.split('@').pop().toLowerCase() : 'unknown';
            acc[domain] = (acc[domain] || 0) + 1;
            return acc;
        }, {});

        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([domain, count]) => ({ domain, Users: count }));
    }, [userList]);

    const providerData = useMemo(() => {
        const counts = userList.reduce((acc, user) => {
            const provider = String(user.provider || 'password').replace('.com', '');
            acc[provider] = (acc[provider] || 0) + 1;
            return acc;
        }, {});

        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 4)
            .map(([provider, count]) => ({ provider, Users: count }));
    }, [userList]);

    const recentUsers = useMemo(() => {
        return [...userList]
            .filter((user) => user.createdAt)
            .sort((a, b) => b.createdAt - a.createdAt)
            .slice(0, 6);
    }, [userList]);

    const featuredSections = useMemo(() => {
        return sections.filter((section) => section.index !== 0);
    }, [sections]);

    const handleUserSearch = () => {
        if (!searchEmail.trim()) return;

        const trimmedEmail = searchEmail.toLowerCase().trim();

        if (bannedEmails.includes(trimmedEmail)) {
            toast.error('This email address is not found');
            return;
        }

        const userId = Object.keys(users).find(id =>
            users[id]?.toLowerCase() === trimmedEmail
        );

        if (userId) {
            setSelectedUserId(userId);
            setOpen(true);
        } else {
            toast.error('User not found');
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') handleUserSearch();
    };

    const metrics = [
        {
            label: 'Total accounts',
            value: formatNumber(overview.total),
            detail: `${formatNumber(overview.newThisMonth)} created this month`,
            icon: Users,
        },
        {
            label: 'Active 30 days',
            value: formatNumber(overview.active30),
            detail: 'Based on last sign-in',
            icon: Activity,
        },
        {
            label: 'Verified users',
            value: `${overview.verificationRate}%`,
            detail: `${formatNumber(overview.verified)} verified accounts`,
            icon: CheckCircle2,
        },
        {
            label: 'Needs attention',
            value: formatNumber(overview.disabled),
            detail: 'Disabled accounts',
            icon: ShieldAlert,
        },
    ];

    return (
        <div className="admin-overview">
            <ModalCustom open={open} setOpen={setOpen}>
                <ShowUserDetails userId={selectedUserId} closeModal={() => {
                    setOpen(false);
                    setSelectedUserId('');
                }} />
            </ModalCustom>

            <div className="admin-metric-grid">
                {metrics.map((metric) => {
                    const Icon = metric.icon;
                    return (
                        <div className="admin-metric-card" key={metric.label}>
                            <div className="admin-metric-icon">
                                <Icon className="h-5 w-5" />
                            </div>
                            <div>
                                <span>{metric.label}</span>
                                <strong>{metric.value}</strong>
                                <p>{metric.detail}</p>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="admin-overview-grid">
                <section className="admin-chart-panel admin-chart-panel-wide">
                    <div className="admin-panel-heading">
                        <div>
                            <p>Growth</p>
                            <h3>Signup trend</h3>
                        </div>
                        <TrendingUp className="h-5 w-5" />
                    </div>
                    <div className="admin-chart-height">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={signupTrend}>
                                <defs>
                                    <linearGradient id="adminUsersGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.28} />
                                        <stop offset="95%" stopColor="#4f46e5" stopOpacity={0.02} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 12 }} tickLine={false} axisLine={false} />
                                <YAxis tick={{ fill: '#64748b', fontSize: 12 }} tickLine={false} axisLine={false} width={28} />
                                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #cbd5e1' }} />
                                <Area type="monotone" dataKey="Users" stroke="#4f46e5" strokeWidth={2} fill="url(#adminUsersGradient)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </section>

                <section className="admin-lookup-panel">
                    <div className="admin-panel-heading">
                        <div>
                            <p>Lookup</p>
                            <h3>User detail search</h3>
                        </div>
                        <User className="h-5 w-5" />
                    </div>
                    <div className="admin-search-row">
                        <div className="admin-search-input">
                            <Search className="h-4 w-4" />
                            <input
                                type="email"
                                value={searchEmail}
                                onChange={(e) => setSearchEmail(e.target.value)}
                                onKeyDown={handleKeyPress}
                                placeholder="customer@company.com"
                            />
                        </div>
                        <button type="button" onClick={handleUserSearch} className="admin-primary-button">
                            <Search className="h-4 w-4" />
                            Search
                        </button>
                    </div>

                    <div className="admin-recent-users">
                        <div className="admin-mini-heading">
                            <CalendarClock className="h-4 w-4" />
                            Recent signups
                        </div>
                        {recentUsers.length > 0 ? recentUsers.map((user) => (
                            <button
                                key={user.userId || user.email}
                                type="button"
                                onClick={() => {
                                    if (!user.userId) return;
                                    setSelectedUserId(user.userId);
                                    setOpen(true);
                                }}
                            >
                                <span>{user.email || user.userId}</span>
                                <em>{user.createdAt?.toLocaleDateString()}</em>
                            </button>
                        )) : (
                            <p className="admin-empty-text">No user data loaded yet.</p>
                        )}
                    </div>
                </section>

                <section className="admin-chart-panel">
                    <div className="admin-panel-heading">
                        <div>
                            <p>Accounts</p>
                            <h3>Top domains</h3>
                        </div>
                    </div>
                    <div className="admin-chart-height-sm">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={domainData} layout="vertical" margin={{ left: 8, right: 12 }}>
                                <CartesianGrid stroke="#e2e8f0" horizontal={false} />
                                <XAxis type="number" tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} />
                                <YAxis dataKey="domain" type="category" width={106} tick={{ fill: '#475569', fontSize: 12 }} axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #cbd5e1' }} />
                                <Bar dataKey="Users" radius={[0, 6, 6, 0]}>
                                    {domainData.map((entry, index) => (
                                        <Cell key={entry.domain} fill={chartColors[index % chartColors.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </section>

                <section className="admin-chart-panel">
                    <div className="admin-panel-heading">
                        <div>
                            <p>Authentication</p>
                            <h3>Provider mix</h3>
                        </div>
                    </div>
                    <div className="admin-provider-list">
                        {providerData.map((item, index) => (
                            <div key={item.provider}>
                                <span>
                                    <i style={{ backgroundColor: chartColors[index % chartColors.length] }} />
                                    {item.provider}
                                </span>
                                <strong>{formatNumber(item.Users)}</strong>
                                <em style={{ width: `${overview.total ? Math.max((item.Users / overview.total) * 100, 4) : 0}%` }} />
                            </div>
                        ))}
                    </div>
                </section>
            </div>

            <section className="admin-section-shortcuts">
                <div className="admin-panel-heading">
                    <div>
                        <p>Workflows</p>
                        <h3>Admin sections</h3>
                    </div>
                </div>

                <div className="admin-shortcut-grid">
                    {featuredSections.map((section) => {
                        const Icon = section.icon;
                        return (
                            <button
                                type="button"
                                key={section.name}
                                onClick={() => setViewNumber(section.index)}
                                className="admin-shortcut-card"
                            >
                                <span className="admin-shortcut-icon">
                                    {Icon ? <Icon className="h-5 w-5" /> : null}
                                </span>
                                <span>
                                    <strong>{section.name}</strong>
                                    <em>{section.description}</em>
                                </span>
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
